import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Menu, MoreHorizontal, Hexagon } from 'lucide-react'
import { Calculator3DIcon, Calendar3DIcon, McpRefresh3DIcon, GlobalRefresh3DIcon, DayNight3DSwitch } from './TopBar3DIcons'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { BadgeVariant } from '@/components/ui/badge'
import { useAppStore } from '@/store'
import { useHealth, useGatewaySession } from '@/hooks/useApi'
import { api } from '@/lib/api'
import { SessionPill, SessionManager } from './WorkerLockManager'
import { ApprovalIndicator } from '@/components/approvals/ApprovalIndicator'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { ClockDisplay } from './ClockDisplay'
import { CalculatorWidget } from './CalculatorWidget'
import { CalendarWidget } from './CalendarWidget'

function roleBadgeVariant(role: string | null): BadgeVariant {
  switch (role) {
    case 'owner': return 'default'
    case 'admin': return 'danger'
    case 'manager': return 'warning'
    case 'developer': return 'info'
    case 'staff': return 'success'
    default: return 'muted'
  }
}

interface TopBarProps {
  onMenuClick?: () => void
  isMobile?: boolean
}

export function TopBar({ onMenuClick, isMobile }: TopBarProps) {
  const { theme, setTheme, activeSection } = useAppStore()
  const { data: health } = useHealth()
  const { data: session } = useGatewaySession()
  const [mcpRefreshing, setMcpRefreshing] = useState(false)
  const [mcpToolCount, setMcpToolCount] = useState<number | null>(null)
  const [mcpSessions, setMcpSessions] = useState<number | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  // Mutually exclusive -- both widgets share the same corner position, so
  // opening one closes the other rather than letting them visually overlap.
  const [activeUtility, setActiveUtility] = useState<'calculator' | 'calendar' | null>(null)

  // Mobile-only: the actionable right-side cluster (lock control, bell,
  // search, utilities, refresh, theme) collapses into a single tap-to-open
  // drawer, per direct request -- the clock stays permanently visible
  // outside it. Desktop is completely untouched, same row as before.
  const [showMobileDrawer, setShowMobileDrawer] = useState(false)
  // Position calculated from the trigger's real screen coordinates, since
  // the dropdown is portaled to document.body -- it no longer shares a DOM
  // ancestor with the trigger, so CSS-relative positioning (absolute
  // top-full) can't apply. Two overflow-hidden ancestors (this header, and
  // the app's own top-level layout wrapper) were clipping the dropdown at
  // the header's bottom edge, which looked exactly like it was rendering
  // behind the page content -- portaling escapes both entirely rather than
  // poking a hole in either, one of which is genuinely foundational to the
  // whole layout's sidebar mechanics.
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null)
  const mobileTriggerRef = useRef<HTMLButtonElement>(null)

  function openMobileDropdown() {
    const rect = mobileTriggerRef.current?.getBoundingClientRect()
    if (rect) {
      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    setShowMobileDrawer(true)
  }

  async function handleMcpRefresh() {
    setMcpRefreshing(true)
    try {
      const [res] = await Promise.all([
        api.mcpToolsRefresh(),
        new Promise(r => setTimeout(r, 5000))
      ])
      setMcpToolCount(res.tool_count)
      setMcpSessions(res.sessions_notified)
      setTimeout(() => { setMcpToolCount(null); setMcpSessions(null) }, 4000)
    } catch { /* silent */ }
    setMcpRefreshing(false)
  }

  function handleGlobalRefresh() {
    setRefreshing(true)
    setTimeout(() => window.location.reload(), 5000)
  }

  const cycleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light'
    setTheme(next)
  }

  const allOperational = health?.status === 'healthy'
  const displayName = session?.display_name ?? 'User'
  const tenantRole = session?.tenant_role ?? null
  const mobileTitle = activeSection === 'dashboard'
    ? 'Platform Overview'
    : activeSection === 'wordpress'
      ? 'WordPress Integration'
      : activeSection === 'architecture'
        ? 'Architecture Vault'
        : activeSection === 'team' || activeSection === 'staffprofiles'
          ? 'Staff Portal'
          : activeSection === 'capabilitycontrol'
            ? 'Capability Control'
            : activeSection
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')

  return (
    <header className={isMobile
      ? "ops-topbar ops-topbar-mobile relative shrink-0 min-w-0 overflow-x-hidden overflow-y-visible"
      : "ops-topbar relative flex items-center justify-between h-14 px-3 border-b border-border shrink-0 min-w-0 overflow-hidden"}>
      {isMobile ? (
        <>
          <div className="ops-topbar-status">
            <div className="ops-topbar-chip">
              <div className={`h-2 w-2 rounded-full shrink-0 ${allOperational ? 'bg-success' : 'bg-warning'}`} />
              <span className={`text-xs font-medium ${allOperational ? 'text-success' : 'text-warning'}`}>
                {allOperational ? 'Healthy' : `${health?.healthy_count ?? '?'}/${health?.total_workers ?? '?'} Healthy`}
              </span>
            </div>

            <div className="ops-worker-lock-anchor ops-worker-lock-anchor-mobile">
              <SessionPill />
              <SessionManager />
            </div>
          </div>
          <div className="ops-topbar-primary">
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
              <Button variant="ghost" size="icon" onClick={onMenuClick} aria-label="Open menu" className="ops-topbar-control shrink-0">
                <Menu className="h-5 w-5" />
              </Button>
              <Hexagon className="ops-topbar-mark h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="ops-topbar-title truncate">{mobileTitle}</span>
            </div>

            {/* The bell, permanent clock and overflow menu stay visible at all times. */}
            <div className="flex items-center gap-0.5 shrink-0 relative z-20">
            <ApprovalIndicator />
            <NotificationBell />
            <ClockDisplay isMobile={isMobile} />
            <Button ref={mobileTriggerRef} variant="ghost" size="icon" onClick={() => showMobileDrawer ? setShowMobileDrawer(false) : openMobileDropdown()} aria-label="More options">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {showMobileDrawer && dropdownPos && createPortal(
              <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setShowMobileDrawer(false)} />
                <div
                  className="fixed w-44 rounded-xl border border-border shadow-xl py-1.5 z-[9999]"
                  style={{ background: 'var(--color-surface)', top: dropdownPos.top, right: dropdownPos.right }}
                >
                  <button
                    onClick={() => { setActiveUtility('calculator'); setShowMobileDrawer(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated transition-colors cursor-pointer"
                  >
                    <Calculator3DIcon className="ops-3d-svg-mobile" /> Calculator
                  </button>
                  <button
                    onClick={() => { setActiveUtility('calendar'); setShowMobileDrawer(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated transition-colors cursor-pointer"
                  >
                    <Calendar3DIcon className="ops-3d-svg-mobile" /> Calendar
                  </button>
                  <button
                    onClick={() => handleMcpRefresh()}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated transition-colors cursor-pointer"
                  >
                    <McpRefresh3DIcon className="ops-3d-svg-mobile" spinning={mcpRefreshing} />
                    {mcpToolCount ? `MCP Refreshed (${mcpToolCount} tools${mcpSessions ? ` · ${mcpSessions} live` : ''})` : 'Refresh MCP'}
                  </button>
                  <button
                    onClick={() => handleGlobalRefresh()}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated transition-colors cursor-pointer"
                  >
                    <GlobalRefresh3DIcon className="ops-3d-svg-mobile" spinning={refreshing} /> Refresh
                  </button>
                  <button
                    onClick={() => cycleTheme()}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated transition-colors cursor-pointer"
                  >
                    <DayNight3DSwitch theme={theme} className="ops-daynight-switch-mobile" /> Theme: {theme}
                  </button>
                </div>
              </>,
              document.body
            )}
            </div>
          </div>


        </>
      ) : (
        <>
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-text-muted">User:</span>
              <span className="font-medium text-text-primary">{displayName}</span>
              {tenantRole && (
                <Badge variant={roleBadgeVariant(tenantRole)}>{tenantRole}</Badge>
              )}
              <Badge variant="success">Production</Badge>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <div className={`h-2 w-2 rounded-full shrink-0 ${allOperational ? 'bg-success animate-pulse' : 'bg-warning animate-pulse'}`} />
              <span className={`text-xs font-medium ${allOperational ? 'text-success' : 'text-warning'}`}>
                {allOperational ? 'All Systems Operational' : `${health?.healthy_count ?? '?'}/${health?.total_workers ?? '?'} Healthy`}
              </span>
            </div>
          </div>

          <div className="ops-worker-lock-anchor ops-worker-lock-anchor-desktop">
            <SessionPill />
            <SessionManager />
          </div>

          <div className="flex items-center gap-0.5 shrink-0 relative z-20">
            <ApprovalIndicator />
            <NotificationBell />
            <ClockDisplay isMobile={isMobile} />
            <Button variant="ghost" size="icon" className="ops-topbar-3d-button" onClick={() => setActiveUtility(u => u === 'calculator' ? null : 'calculator')} aria-label="Calculator">
              <Calculator3DIcon />
            </Button>
            <Button variant="ghost" size="icon" className="ops-topbar-3d-button" onClick={() => setActiveUtility(u => u === 'calendar' ? null : 'calendar')} aria-label="Calendar">
              <Calendar3DIcon />
            </Button>
            <Button variant="ghost" size="icon" className="ops-topbar-3d-button" onClick={handleMcpRefresh} aria-label="Refresh MCP">
              <McpRefresh3DIcon spinning={mcpRefreshing} />
            </Button>
            {mcpToolCount && <span className="text-xs text-accent font-medium">{mcpToolCount} tools{mcpSessions ? ` · ${mcpSessions} live` : ''}</span>}
            <Button variant="ghost" size="icon" className="ops-topbar-3d-button" aria-label="Refresh" onClick={() => handleGlobalRefresh()}>
              <GlobalRefresh3DIcon spinning={refreshing} />
            </Button>
            <Button variant="ghost" className="ops-theme-switch-hitbox" onClick={cycleTheme} aria-label={`Theme: ${theme}`} title={`Theme: ${theme}`}>
              <DayNight3DSwitch theme={theme} />
            </Button>
          </div>
        </>
      )}


      <CalculatorWidget open={activeUtility === 'calculator'} onClose={() => setActiveUtility(null)} />
      <CalendarWidget open={activeUtility === 'calendar'} onClose={() => setActiveUtility(null)} />
    </header>
  )
}
